'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { invalidateHistory } from '@/src/queries/keys/history'
import { useTrade } from '@/src/stores/route-machine/actions'
import {
  isRetryPossibleAtom,
  routeMachineAtom,
  routeMachineSnapshotAtom,
} from '@/src/stores/route-machine/atoms'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import { useSwapFormAmountReset } from '@/src/stores/swap/actions'
import { Button, type ButtonProps } from '../ui/button'

const ConnectButton = () => {
  const { ampli } = useAmplitudeAnalytics()
  const setDialogState = useSetAtom(selectWalletsDialogAtom)

  const onClick = useCallback(() => {
    ampli?.walletConnectWalletClicked({
      position: 'confirmation-screen',
    })
    setDialogState(openSelectWalletsDialog())
  }, [ampli, setDialogState])

  return (
    <Button
      className='w-full whitespace-break-spaces'
      variant='primary'
      size='lg'
      onClick={onClick}
    >
      Connect Wallet
    </Button>
  )
}

const ConfirmationButtons = () => {
  const { reset } = useSwapFormAmountReset()
  const { goBack } = useTrade()
  const actorRef = useAtomValue(routeMachineAtom)

  const onClickGoBack = useCallback(() => {
    goBack()
    reset()
  }, [goBack, reset])

  const onClickConfirm = useCallback(() => {
    actorRef.send({ type: 'confirm' })
  }, [actorRef])

  return (
    <div className='flex items-center gap-3'>
      <Button
        className='flex-1'
        variant='secondary'
        onClick={onClickGoBack}
        size='lg'
      >
        Cancel
      </Button>
      <Button
        className='flex-1'
        variant='primary'
        onClick={onClickConfirm}
        size='lg'
      >
        Confirm
      </Button>
    </div>
  )
}

export const DismissButton = (
  props: ButtonProps & { onDismiss?: () => void },
) => {
  const { reset } = useSwapFormAmountReset()
  const { onDismiss, ...buttonProps } = props

  const onClick = useCallback(() => {
    onDismiss?.()
    reset()
    invalidateHistory()
  }, [reset, onDismiss])

  return (
    <Button
      className='w-full'
      variant='secondary'
      onClick={onClick}
      size='lg'
      {...buttonProps}
    />
  )
}

export const RetryButton = () => {
  const actorRef = useAtomValue(routeMachineAtom)
  const snapshot = useAtomValue(routeMachineSnapshotAtom)

  const onClick = useCallback(
    () => actorRef.send({ type: 'retry' }),
    [actorRef],
  )

  const canRetry =
    snapshot.hasTag('retryable') && snapshot.context.maxRetries > 0

  return (
    <Button
      className='w-full whitespace-break-spaces'
      variant='primary'
      size='lg'
      disabled={!canRetry}
      onClick={onClick}
    >
      Retry
    </Button>
  )
}

export const ActionsButton = () => {
  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const isRetryPossible = useAtomValue(isRetryPossibleAtom)
  const { clearRoute, goBack } = useTrade()
  const pathname = usePathname()
  const isRoutePage = pathname.startsWith('/route')

  const buttonToRender = useMemo(() => {
    if (snapshot.matches('WalletDisconnected')) {
      return <ConnectButton />
    }

    if (snapshot.value === 'Idle') {
      return <ConfirmationButtons />
    }

    if (
      snapshot.value === 'Success' ||
      snapshot.value === 'FundsRecoverySuccess' ||
      snapshot.context.route?.isExpired
    ) {
      return (
        <DismissButton
          variant='primary'
          onDismiss={isRoutePage ? goBack : clearRoute}
        >
          Done
        </DismissButton>
      )
    }

    if (
      snapshot.context.error ||
      snapshot.value === 'CurrentAutopilotStepFailed'
    ) {
      return (
        <div className='flex flex-col gap-3'>
          {isRetryPossible && <RetryButton />}
          <DismissButton onDismiss={clearRoute}>Cancel</DismissButton>
        </div>
      )
    }

    // Show "Close" only if route/trade is being executed (after first message)
    const isExecuting = snapshot.context.isTradeStarted
    return (
      // If the route is already executed, simply go back to the previous
      // page but keep it in the swap tool.
      // Otherwise, clear the route so that a new one can be started.
      <DismissButton onDismiss={isRoutePage ? goBack : clearRoute}>
        {isExecuting ? 'Close' : 'Cancel'}
      </DismissButton>
    )
  }, [snapshot, isRetryPossible, isRoutePage, clearRoute, goBack])

  if (buttonToRender !== null)
    return <div className='w-full pt-3 pb-5 px-5'>{buttonToRender}</div>
  return <div className='pb-5' />
}
